import { datadogRum } from '@datadog/browser-rum';
import { UserEntity } from '@/generated/nabisNYWebAPI.schemas';

interface AnalyticsIdentifyEvent {
  payload: AnalyticsIdentifyPayload;
}

interface AnalyticsTrackEvent {
  payload: AnalyticsTrackPayload;
}

interface AnalyticsPageEvent {
  payload: AnalyticsPagePayload;
}

interface AnalyticsIdentifyPayload {
  userId: string;
  traits?: Partial<UserEntity>;
}

interface AnalyticsTrackPayload {
  event: string;
  properties?: object;
}

interface AnalyticsPagePayload {
  properties: {
    path: string;
    search?: string;
  };
  userId: string;
}

export interface Config {
  applicationId?: string;
  clientToken?: string;
  env?: string;
  version?: string;
}

// https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
const apiUrl = process.env.NEXT_PUBLIC_API_DOMAIN ?? 'https://ny-dev-api.nabis.dev';

export function analyticsDataDogPlugin(config: Config) {
  let isLoaded = false;

  function parseXHRResponse(xhrResponseText: string) {
    try {
      return JSON.parse(xhrResponseText) as object;
    } catch (err) {
      return xhrResponseText;
    }
  }

  return {
    name: 'datadogRum',
    initialize: () => {
      datadogRum.onReady(() => {
        isLoaded = true;
      });

      if (config.applicationId && config.clientToken) {
        datadogRum.init({
          silentMultipleInit: true,
          applicationId: config.applicationId,
          clientToken: config.clientToken,
          site: 'datadoghq.com',
          service: 'new-york-ui',
          env: config.env,
          version: config.version,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          trackViewsManually: true,
          defaultPrivacyLevel: 'allow',
          allowedTracingUrls: [apiUrl],
          beforeSend: (event, context) => {
            if (!event.context) {
              event.context = {};
            }
            if (event.type === 'resource' && event.resource.type === 'xhr' && 'xhr' in context) {
              event.context.responseStatus = context.xhr.status;
              if (context.xhr.responseType === '' || context.xhr.responseType === 'text') {
                event.context.responseBody = parseXHRResponse(context.xhr.responseText);
              } else {
                event.context.responseBody = '[unsupported responseType]';
              }
            }
          },
        });

        datadogRum.startSessionReplayRecording();
      }
    },
    page: ({ payload }: AnalyticsPageEvent) => {
      let href = payload?.properties?.path;
      if (payload?.properties?.search) {
        href += payload?.properties?.search;
      }

      datadogRum.addAction(href);
    },
    track: ({ payload }: AnalyticsTrackEvent) => {
      datadogRum.addAction(payload?.event, payload.properties);
    },
    identify: ({ payload }: AnalyticsIdentifyEvent) => {
      datadogRum.setUser({
        ...(payload?.traits || {}),
        id: payload?.userId,
        email: payload?.traits?.email,
        name: `${payload?.traits?.firstName ?? ''} ${payload?.traits?.lastName ?? ''}`,
      });
    },
    reset: () => {
      datadogRum.removeUser();
    },
    loaded: () => {
      return isLoaded;
    },
    methods: {
      error: (err: Error) => {
        datadogRum.addError(err);
      },
    },
  };
}
